<script setup lang="ts">
import { type Charge } from '@/api/charges';
import { formatCurrency } from '@/utils/currency';
import { chargeTaxDocumentButtonProps } from '@/utils/tax-documents';

// Phone rendering of a /charges row: tap the body to open the charge detail;
// the icon row mirrors the desktop per-row actions (stop propagation so they
// never also fire the card's open).
interface Props {
  charge: Charge;
  canEmitTaxDocument?: boolean;
  // Off for companies billing through Kuido: the DTE email replaces the
  // manual email and WhatsApp sends.
  canSendCharges?: boolean;
  // charges/show: the PDF and the WhatsApp link both hand out the document.
  canShowDocument?: boolean;
}

withDefaults(defineProps<Props>(), { canEmitTaxDocument: false, canSendCharges: true, canShowDocument: true });

const emit = defineEmits<{
  open: [];
  pdf: [];
  email: [];
  whatsapp: [];
  taxDocument: [];
}>();
</script>
<template>
  <v-card
    v-ripple
    variant="elevated"
    :elevation="1"
    class="pa-3"
    @click="emit('open')"
  >
    <div class="d-flex align-center ga-2">
      <span class="font-weight-bold text-body-1 text-truncate flex-grow-1">
        {{ charge.patient.fullName }}
      </span>
      <v-chip
        color="green-darken-3"
        size="small"
        class="flex-shrink-0"
      >
        {{ formatCurrency(charge.amountPendingToCharge) }}
      </v-chip>
    </div>
    <div class="text-body-2 text-medium-emphasis text-truncate mt-1">
      {{ charge.patient.representative?.fullName ?? 'Sin apoderado' }}
    </div>
    <div class="d-flex flex-wrap ga-4 mt-2 text-body-2">
      <span>
        <span class="text-medium-emphasis">Turnos:</span> {{ charge.shiftsCount }}
      </span>
      <span>
        <span class="text-medium-emphasis">Monto turnos:</span> {{ formatCurrency(charge.price) }}
      </span>
      <span v-if="charge.totalChargeMovements">
        <span class="text-medium-emphasis">Ajustes:</span> {{ formatCurrency(charge.totalChargeMovements) }}
      </span>
    </div>
    <div class="d-flex justify-end ga-1 mt-1">
      <v-btn
        v-if="canShowDocument"
        icon="mdi-printer"
        size="small"
        variant="text"
        density="comfortable"
        @click.stop="emit('pdf')"
      />
      <v-btn
        v-if="canSendCharges && charge.patient.billingRecipients?.length"
        icon="mdi-email-arrow-right"
        size="small"
        variant="text"
        density="comfortable"
        @click.stop="emit('email')"
      />
      <v-btn
        v-if="canShowDocument && canSendCharges && charge.patient.representative?.phoneNumber"
        icon="mdi-whatsapp"
        size="small"
        variant="text"
        density="comfortable"
        @click.stop="emit('whatsapp')"
      />
      <v-btn
        v-if="canEmitTaxDocument"
        icon="mdi-file-document-check-outline"
        size="small"
        density="comfortable"
        v-bind="chargeTaxDocumentButtonProps(charge.taxDocumentStatus, 'text')"
        @click.stop="emit('taxDocument')"
      />
    </div>
  </v-card>
</template>
